/**
 * ThemeContext - Manejo de tema claro/oscuro/automatico (NER-1281)
 *
 * Caracteristicas:
 * - Preferencia ternaria: 'light' | 'dark' | 'auto' (default 'auto').
 * - `preference` = lo que eligio el usuario; `resolvedTheme` = light/dark final.
 * - En modo 'auto' se suscribe a prefers-color-scheme y reacciona en vivo.
 * - Persiste SOLO la preferencia (no el tema resuelto), prefijada `admin_`.
 * - Migra la clave legacy binaria `digital-menu-theme`.
 * - Aplica la clase `dark` en <html> (selector :root.dark del CSS).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { storage } from '../utils/storage';
import {
  PREFERENCE_KEY,
  LEGACY_KEY,
  AUTO_MODE_KEY,
  resolveTheme,
  nextPreference,
  isValidPreference,
  isValidAutoMode,
  resolveInitialPreference,
  resolveInitialAutoMode,
} from '../utils/theme';

const ThemeContext = createContext();

function getSystemPrefersDark() {
  return !!(
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

export function ThemeProvider({ children }) {
  // Preferencia inicial: clave nueva > migracion legacy > default 'auto'.
  const [preference, setPreferenceState] = useState(() => {
    const { preference: initial } = resolveInitialPreference(
      storage.get(PREFERENCE_KEY),
      storage.get(LEGACY_KEY)
    );
    return initial;
  });

  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);

  // P2 (NER-1564): sub-modo de 'auto' ('system' | 'time-of-day').
  const [autoMode, setAutoModeState] = useState(() =>
    resolveInitialAutoMode(storage.get(AUTO_MODE_KEY))
  );

  // Hora local actual, refrescada por intervalo solo en modo 'time-of-day'
  // para cruzar los limites diurno/nocturno (7:00 / 19:00) sin recargar.
  const [nowHour, setNowHour] = useState(() => new Date().getHours());

  const resolvedTheme = resolveTheme(preference, systemPrefersDark, {
    autoMode,
    hour: nowHour,
  });

  // Limpieza one-time de la clave legacy (la migracion del valor ya ocurrio
  // en el initializer; la persistencia de la clave nueva la hace el effect de abajo).
  useEffect(() => {
    if (storage.get(LEGACY_KEY)) {
      storage.remove(LEGACY_KEY);
    }
  }, []);

  // Aplicar tema resuelto al documento.
  useEffect(() => {
    const root = document.documentElement;
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolvedTheme]);

  // Persistir SOLO la preferencia.
  useEffect(() => {
    storage.set(PREFERENCE_KEY, preference);
  }, [preference]);

  // Persistir el sub-modo de 'auto'.
  useEffect(() => {
    storage.set(AUTO_MODE_KEY, autoMode);
  }, [autoMode]);

  // P2: en 'auto' + 'time-of-day', refrescar la hora cada 5 min. La transicion
  // al cruzar el limite horario es smooth (re-resolve al proximo tick), no
  // necesita ser instantanea. En cualquier otro modo no hay timer activo.
  useEffect(() => {
    if (preference !== 'auto' || autoMode !== 'time-of-day') return undefined;

    const tick = () => setNowHour(new Date().getHours());
    tick(); // sincronizar inmediatamente al entrar al modo
    const intervalId = setInterval(tick, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [preference, autoMode]);

  // Suscribirse a prefers-color-scheme SOLO cuando la preferencia es 'auto'.
  useEffect(() => {
    if (preference !== 'auto') return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setSystemPrefersDark(e.matches);

    // Sincronizar por si el sistema cambio antes de suscribirnos.
    setSystemPrefersDark(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preference]);

  const setPreference = useCallback((next) => {
    if (isValidPreference(next)) {
      setPreferenceState(next);
    }
  }, []);

  const cyclePreference = useCallback(() => {
    setPreferenceState((prev) => nextPreference(prev));
  }, []);

  const setAutoMode = useCallback((next) => {
    if (isValidAutoMode(next)) {
      setAutoModeState(next);
    }
  }, []);

  const isDark = resolvedTheme === 'dark';

  const value = {
    preference,
    resolvedTheme,
    setPreference,
    cyclePreference,
    autoMode,
    setAutoMode,
    // Aliases backward-compatible (consumidores previos: SettingsPage / Header).
    theme: preference,
    setTheme: setPreference,
    toggleTheme: cyclePreference,
    isDark,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
